import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { onResize } from '@coin/shared/core/util-helpers';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';
import ChatMascotComponent from '../chat-mascot/chat-mascot.component';
import { PromptButtonComponent } from '../prompt-button/prompt-button.component';

@Component({
  selector: 'coin-chat-start-view',
  templateUrl: './chat-start-view.component.html',
  styleUrls: ['./chat-start-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChatMascotComponent, V2ButtonComponent, NgTemplateOutlet, TranslateModule, PromptButtonComponent]
})
export class ChatStartViewComponent {
  _elementRef = viewChild('container', { read: ElementRef });
  _destroyRef = inject(DestroyRef);

  prompts = input<string[]>([]);
  name = input<string>('');
  newPromptLabel = input<string>('chat-bot.actions.another-suggestion');

  promptSelected = output<string>();

  selectedPrompts = signal<string[]>([]);

  currentSize = signal<{ width: number; height: number }>({
    width: 0,
    height: 0
  });

  totalPrompts = computed(() => {
    const { height } = this.currentSize();
    // if height is less then 400px, then we can show 2 prompts, otherwise 3
    let promptsCount = height < 400 ? 2 : 3;
    promptsCount = height < 200 ? 1 : promptsCount;
    return Math.min(this.prompts().length, promptsCount);
  });

  userName = computed(() => {
    if (this.name()) {
      return ` ${this.name()}`;
    }
    return '';
  });

  containerSize = computed<'small' | '' | null>(() => {
    const { width, height } = this.currentSize();
    if (!width || !height) return null; // don’t render yet

    return height < 768 ? 'small' : '';
  });

  constructor() {
    effect(() => {
      this.getSelectedPrompts();
      this.selectedPrompts.set(this.getSelectedPrompts());
    });

    effect(() => {
      const elem = this._elementRef();

      if (elem) {
        onResize(elem.nativeElement)
          .pipe(debounceTime(250), takeUntilDestroyed(this._destroyRef))
          .subscribe(entry => {
            const { contentRect } = entry;
            this.currentSize.set({
              width: contentRect.width,
              height: contentRect.height
            });
          });
      }
    });
  }

  generateNewPrompts() {
    this.selectedPrompts.set(this.getSelectedPrompts());
  }

  private getSelectedPrompts() {
    return this.prompts()
      .sort(() => 0.5 - Math.random())
      .slice(0, this.totalPrompts());
  }
}
